import { Injectable } from '@angular/core';

import { Observable, Subscription, of } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { DistributionSummaryApiModel } from './model/distribution-summary-api.model';
import { DistributionApiModel } from './model/distribution-api.model';
import { TargetingQueryApi } from '../targeting/model/targeting-search.api.model';
import { LinkStatsApiModel } from '../../Common/TargetingShared/model/statisticsReport/link-stats-api.model';
import { RecipientSearchModel } from '../../Discover/Targeting/components/Distribution/DistributionReport/RecipientSearch/recipient-search.model';
import { SortOrderEnum } from '../shared/enums/sort-order.enum';
import { RecipientSearchResultsApiModel } from './model/recipient-search-results.api.model';
import { ResourceBodyApiModel } from './model/distribution-body.api.model';
import { TestEmailApiModel } from './model/test-email-api.model';
import { DistributionFooterApiModel } from './model/distribution-footer.api.model';
import { AmplificationStatusAPIEnum } from './enum/amplification-status-api.enum';
import { NeptuneApi } from '../genericApi/neptune.api';
import { DistributionAccountStatsApiModel } from './model/distribution-account-stats.api.model';
import { DistributionAccountStatisticsDateRange } from './model/distribution-account-statistics-date-range.api.model';
import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';
import { CsvExportField } from '../../Discover/Targeting/components/TargetingResults/CsvExportDialog/csv-export-field.enum';
import { AddAllRecipientsToListsApiModel } from './model/add-all-recipients-to-lists.api.model';
import { MultiBaseAbstractApi } from '../genericApi/multi-base-abstract.api';
import {
    DistributionRecipientReportApiModel,
    DistributionReportStat
} from '../../Common/TargetingShared/model/statisticsReport/distribution-report-stat-api.model';
import {
    EventsOverTimeApiModel,
    EventsOverTimePoint,
    EventsOverTimeQueryApiModel
} from '../../Common/TargetingShared/model/statisticsReport/events.over.time.api.model';
import { DisplayGroupPatchModel } from '../displayGroups/models/display-group-patch.api.model';
import { EntityIdApiModel } from '../targeting/model/entity-id-api-model';

import { PersonalizationTagEnum } from '../../Discover/Targeting/enums/personalization-tag.enum';
import {
    DistributionRecipientQuery,
    PersonalizationDetailsApiModel,
    PersonalizationListApi,
    PersonalizationSelection
} from './model/distribution-personalization.api.model';
import { ToggleAction } from '../../Discover/Targeting/enums/toggle-action.enum';
import { TargetingListApi } from '../targeting/model/targeting-list-api.model';
import { RecipientNegativeoutcomeSearchModel } from '../../Discover/Targeting/models/recipients-negativeoutcome-search.model';
import {
    PersonalizationSearchResponseApiModel,
    PersonalizationSummaryApiModel,
    PersonalizationValue
} from './model/distribution-personalization.api.model';
import { TargetingSearchResponseApiModel } from '../targeting/model/targeting-search-response.api.model';
import { AEORecommendationsApiModel } from './model/aeo-recommendations.api.model';

@Injectable()
export class DistributionApi extends MultiBaseAbstractApi<DistributionApiModel, DistributionSummaryApiModel> {
    private locale: string = 'en-US';

    constructor(
        private neptuneApi: NeptuneApi,
        private currentUserStateService: CurrentUserStateService
    ) {
        super();
        this.currentUserStateService.getDataReferenceLanguageTag().pipe(
            filter(x => !!x)
        ).subscribe(languageTag => {
            this.locale = languageTag;
        });
    }

    getList(): Observable<DistributionSummaryApiModel[]> {
        const slug: string = '/distributions/';

        return this.neptuneApi.api<DistributionSummaryApiModel[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    getById(id: number): Observable<DistributionApiModel> {
        const slug: string = `/distributions/${id}`;

        return this.neptuneApi.api<DistributionApiModel>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    save(distribution: DistributionApiModel): Observable<number> {
        if (distribution.id) {
            return this.update(distribution).pipe(map(() => distribution.id));
        } else {

            return this.create(distribution);
        }
    }

    create(distribution: DistributionApiModel): Observable<number> {
        const slug: string = '/distributions/';

        return this.neptuneApi.api<number>(
            slug,
            distribution,
            HttpVerbsEnum.POST
        );
    }

    update(distribution: DistributionApiModel): Observable<number> {
        const slug: string = `/distributions/${distribution.id}`;

        return this.neptuneApi.api<number>(
            slug,
            distribution,
            HttpVerbsEnum.PUT
        );
    }

    updateSentDistribution(distribution: DistributionSummaryApiModel): Observable<void> {
        const slug: string = `/distributions/${distribution.id}`;

        return this.neptuneApi.api<void>(
            slug,
            distribution,
            HttpVerbsEnum.PATCH
        );
    }

    scheduleDistribution(id: number, body: ResourceBodyApiModel): Observable<number> {
        const slug: string = `/distributions/${id}/schedule?locale=${this.locale}`;

        return this.neptuneApi.api<number>(
            slug,
            body,
            HttpVerbsEnum.POST
        );
    }

    sendTestDistribution(email: TestEmailApiModel): Observable<number> {
        const slug: string = '/distributions/testemail';

        return this.neptuneApi.api<number>(
            slug,
            email,
            HttpVerbsEnum.POST
        );
    }

    deleteById(id: number) {
        const slug: string = `/distributions/${id}`;

        return this.neptuneApi.api<number>(
            slug,
            null,
            HttpVerbsEnum.DELETE
        );
    }

    cancelDistribution(id: number): Observable<number> {
        const slug: string = `/distributions/${id}/cancel`;

        return this.neptuneApi.api<number>(
            slug,
            null,
            HttpVerbsEnum.POST
        );
    }

    getResourceReport(id: number, query?: TargetingQueryApi): Observable<DistributionRecipientReportApiModel> {
        const slug: string = `/distributions/report/${id}/recipientreport`;

        return this.neptuneApi.api<DistributionRecipientReportApiModel>(
            slug,
            query,
            HttpVerbsEnum.POST
        );
    }

    getEventsOverTime(id: number, query: EventsOverTimeQueryApiModel): Observable<EventsOverTimeApiModel> {
        const slug: string = `/distributions/${id}/eventsovertime`;

        return this.neptuneApi.api<EventsOverTimeApiModel>(
            slug,
            query,
            HttpVerbsEnum.POST
        );
    }

    getQuotaOverTime(): Observable<EventsOverTimePoint[]> {
        const slug: string = '/distributions/quotaovertime';

        return this.neptuneApi.api<EventsOverTimePoint[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    getLinksReport(id: number): Observable<LinkStatsApiModel[]> {
        const slug: string = `/distributions/${id}/linksreport`;

        return this.neptuneApi.api<LinkStatsApiModel[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    getResourceBody(id: number): Observable<ResourceBodyApiModel> {
        const slug: string = `/distributions/report/${id}/body`;

        return this.neptuneApi.api<any>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * perform recipient search for sent distribution
     */
    performRecipientSearch(
        distributionId: number,
        search: RecipientSearchModel,
        start: number,
        rows: number,
        sortOrder: SortOrderEnum,
        sortField: string
    ): Observable<RecipientSearchResultsApiModel> {
        const slug: string = `/distributions/report/${distributionId}/searchrecipients`;

        return this.neptuneApi.api<RecipientSearchResultsApiModel>(
            slug,
            // TODO : Flesh out
            {
                link: search.link,
                statsType: search.statType,
                listId: search.listId,
                searchId: search.searchId,
                query: search.query
            },
            HttpVerbsEnum.POST,
            {
                rows: rows,
                start: start,
                sortBy: sortField,
                sortOrder: sortOrder
            }
        );
    }

    performAdditionalRecipientSearch(
        distributionId: number,
        search: RecipientSearchModel
    ): Observable<RecipientSearchResultsApiModel> {
        const slug: string = `/distributions/report/${distributionId}/searchadditionalrecipients`;

        return this.neptuneApi.api<RecipientSearchResultsApiModel>(
            slug,
            {statsType: search.statType},
            HttpVerbsEnum.POST
        );
    }

    /**
     * perform recipient search for unsent distribution
     */
    performDraftDistributionRecipientSearch(distributionId: number, filterQuery: TargetingQueryApi): Observable<RecipientSearchResultsApiModel> {
        const slug: string = `/distributions/${distributionId}/searchmembers`;

        return this.neptuneApi.api<RecipientSearchResultsApiModel>(
            slug,
            filterQuery,
            HttpVerbsEnum.POST
        );
    }

    exportAllRecipients(
        distributionId: number,
        fields: CsvExportField[],
        query: TargetingQueryApi,
        statType: DistributionReportStat,
        link: string
    ): Observable<string> {
        const slug: string = `/distributions/report/${distributionId}/recipients/export`;

        return this.neptuneApi.api<string>(
            slug,
            {
                fields: fields,
                query: query,
                statsType: statType,
                link: link
            },
            HttpVerbsEnum.POST,
            {},
            'text'
        );
    }

    /**
     * Search a distribution's recipients that have a negative outcome - Unsubscribed, Spam Reports, and Hard Bounce, etc.
     */
    getNegativeOutcomeRecipients(
        distributionId: number,
        search: RecipientNegativeoutcomeSearchModel,
        start: number,
        rows: number
    ): Observable<RecipientSearchResultsApiModel> {
        const slug: string = `/distributions/report/${distributionId}/searchrecipients/negativeoutcome`;
        return this.neptuneApi.api<RecipientSearchResultsApiModel>(
            slug,
            search,
            HttpVerbsEnum.POST,
            { start, rows }
        );
    }

    /**
     * Get all lists of a distribution's recipients that have a negative outcome - Unsubscribed, Spam Reports, and Hard Bounce, etc.
     */
    getNegativeOutcomeLists(
        distributionId: number,
        search: RecipientNegativeoutcomeSearchModel,
    ): Observable<TargetingListApi[]> {
        const slug: string = `/distributions/report/${distributionId}/searchrecipients/negativeoutcome/lists`;
        return this.neptuneApi.api<TargetingListApi[]>(
            slug,
            search,
            HttpVerbsEnum.POST
        );
    }

    /**
     * Remove entities from lists using the recipients with negative outcome search
     */
    removeNegativeOutcomeRecipientsFromLists(
        distributionId: number,
        query: RecipientNegativeoutcomeSearchModel,
        listIds: number[]
    ): Observable<void> {
        const slug: string = `/distributions/report/${distributionId}/searchrecipients/negativeoutcome/removeentities`;
        return this.neptuneApi.api<void>(
            slug,
            {
                query,
                listIds
            },
            HttpVerbsEnum.POST
        );
    }

    /**
     * Adds all entities from search context to the distribution as recipients
     */
    addAllRecipientsToLists(distributionId: number, searchContext: AddAllRecipientsToListsApiModel) {
        const slug: string = `/distributions/report/${distributionId}/targetinglists/addrecipients`;

        return this.neptuneApi.api<void>(
            slug,
            searchContext,
            HttpVerbsEnum.POST
        );
    }

    removeAllRecipientsFromLists(
        distributionId: number,
        query: TargetingQueryApi,
        statType: DistributionReportStat,
        link: string,
        listIds: number[]
    ) {
        const slug: string = `/distributions/report/${distributionId}/targetinglists/removerecipients`;

        return this.neptuneApi.api<void>(
            slug,
            {
                query: query,
                statsType: statType,
                link: link,
                listIds: listIds
            },
            HttpVerbsEnum.POST
        );
    }

    getDistributionRecipientCount(distributionId: number): Observable<number> {
        const slug: string = `/distributions/${distributionId}/members/count`;

        return this.neptuneApi.api<number>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    setDefaultFooter(footer: DistributionFooterApiModel) {
        const slug: string = '/distributions/defaultfooter';

        return this.neptuneApi.api<any>(
            slug,
            footer,
            HttpVerbsEnum.POST
        );
    }

    getDefaultFooter(): Observable<DistributionFooterApiModel> {
        const slug: string = '/distributions/defaultfooter';

        return this.neptuneApi.api<DistributionFooterApiModel>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    getDistributionPostOptions(distributionId: number): Observable<AmplificationStatusAPIEnum[]> {
        const slug: string = `/distributions/${distributionId}/distributionPostOptions`;

        return this.neptuneApi.api<AmplificationStatusAPIEnum[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    setDistributionPostOptions(distributionId: number, amplifications: AmplificationStatusAPIEnum[]) {
        const slug: string = `/distributions/${distributionId}/distributionPostOptions`;

        return this.neptuneApi.api<any>(
            slug,
            amplifications,
            HttpVerbsEnum.POST
        );
    }

    getDistributionAccountStats(dateRange: DistributionAccountStatisticsDateRange): Observable<DistributionAccountStatsApiModel> {
        const slug: string = '/distributions/accountStats';

        return this.neptuneApi.api<DistributionAccountStatsApiModel>(
            slug,
            dateRange,
            HttpVerbsEnum.POST
        );
    }

    addRecipientList(distroId: number, listId: number) {
        return this.neptuneApi.api<DistributionAccountStatsApiModel>(
            `/distributions/${distroId}/addrecipientlist?listId=${listId}`,
            null,
            HttpVerbsEnum.POST
        );
    }

    getTemplates(): Observable<DistributionSummaryApiModel[]> {
        const slug: string = '/distributions/templates';

        return this.neptuneApi.api<DistributionSummaryApiModel[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    getRecommendedRecipientsQueryByText(text: string): Observable<TargetingQueryApi> {
        const slug: string = '/distributions/recommendedrecipientsquery';

        return this.neptuneApi.api<TargetingQueryApi>(
            slug,
            {
                text: text
            },
            HttpVerbsEnum.POST
        );
    }

    getRecommendedRecipientsQueryByDistribution(distributionId: number): Observable<TargetingQueryApi> {
        const slug: string = `/distributions/${distributionId}/recommendedrecipientsquery`;

        return this.neptuneApi.api<TargetingQueryApi>(
            slug,
            HttpVerbsEnum.GET
        );
    }

    /**
     * Adds and/or removes distributions from provided group
     * @param addIds Distributions to add to group
     * @param removeIds Distributions to remove from group
     * @param groupId
     * @returns
     */
    updateDistributionDisplayGroup(addIds: number[], removeIds: number[], groupId: number): Observable<void> {
        const slug: string = `/distributions/displaygroups/${groupId}`;
        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    /**
     * Removes group ID from all provided distributions
     * @param removeIds Distributions to remove from groups
     * @returns
     */
    removeDistributionsFromDisplayGroup(removeIds: number[]): Observable<void> {
        const slug: string = '/distributions/displaygroups';
        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: [],
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    /**
     * Adds/ removes distributions from favorites group
     * @param addIds Distributions to add to favorites
     * @param removeIds Distributions to remove from favorites
     * @returns
     */
    updateDistributionFavorites(addIds: number[], removeIds: number[]): Observable<void> {
        const slug: string = '/distributions/displaygroups/favorites';
        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }


    addSearchResultsToDist(ids: number[], targetingQuery: TargetingQueryApi): Observable<void> {
        const url: string = `/distributions/addsearchresults?locale=${this.locale}`;
        return this.neptuneApi.api<void>(
            url,
            {
                distributionIds: ids,
                query: targetingQuery
            },
            HttpVerbsEnum.POST
        );
    }

    addEntitiesToDist(ids: number[], entityIds: EntityIdApiModel[]): Observable<void> {
        const slug: string = '/distributions/addentities';

        return this.neptuneApi.api<void>(
            slug,
            {
                distributionIds: ids,
                entityIds: entityIds
            },
            HttpVerbsEnum.POST
        );
    }


    /**
     * Perform a distribution recipient personalization search for a specific personalization with filtering, sorting, paging
     */
    personalizationSearch(
        distributionId: number,
        search: DistributionRecipientQuery,
        start: number = 0,
        rows: number,
        sortOrder: SortOrderEnum,
        sortField: string
    ): Observable<PersonalizationSearchResponseApiModel> {
        const slug: string = `/distributions/${distributionId}/personalizations/search?start=${start}&rows=${rows}&sortOrder=${sortOrder}&sortBy=${sortField}`;

        return this.neptuneApi.api<PersonalizationSearchResponseApiModel>(
            slug,
            search,
            HttpVerbsEnum.POST
        );
    }


    /**
     * Lock or Unlock searches that are recipients of a distribution
     */
    toggleRecipientSearch(distributionId: number, action: ToggleAction, searchIds: number[]): Observable<void> {
        const slug: string = `/distributions/${distributionId}/togglerecipients`;

        return this.neptuneApi.api<any>(
            slug,
            {
                action: action,
                searchIds: searchIds
            },
            HttpVerbsEnum.POST
        );
    }


    /**
     * Get summary stats of all personalizations for the distributions
     */
    getPersonalizationSummary(distributionId: number, includeUnlocked: boolean = false): Observable<PersonalizationSummaryApiModel> {
        const slug: string = `/distributions/${distributionId}/personalizations/details?includeUnlockedSearches=${includeUnlocked}`;

        return this.neptuneApi.api<any>(
            slug,
            null,
            HttpVerbsEnum.GET
        ).pipe(
            map(personalizationSummary => {
                const cleanedPersonalizations = personalizationSummary;
                cleanedPersonalizations.details = cleanedPersonalizations.details.filter(x => Object.values(PersonalizationTagEnum).includes(x.tagType));
                return cleanedPersonalizations;
            })
        );
    }


    getContactPersonalizations(
        distributionId: number,
        contactUuid: string,
    ): Observable<PersonalizationListApi> {
        const slug: string = `/distributions/${distributionId}/personalizations/contact/${contactUuid}`;

        return this.neptuneApi.api<PersonalizationListApi>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    getOutletPersonalizations(
        distributionId: number,
        outletUuid: string,
    ): Observable<PersonalizationListApi> {
        const slug: string = `/distributions/${distributionId}/personalizations/outlet/${outletUuid}`;

        return this.neptuneApi.api<PersonalizationListApi>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    /** Only applies to PersonalizationSourceTypeEnum.CUSTOM */
    updateContactPersonalization(
        distributionId: number,
        contactUuid: string,
        personalization: PersonalizationValue
    ): Observable<void> {
        const slug: string = `/distributions/${distributionId}/personalizations/contact/${contactUuid}`;

        return this.neptuneApi.api<void>(
            slug,
            personalization,
            HttpVerbsEnum.PUT
        );
    }

    /** Only applies to PersonalizationSourceTypeEnum.CUSTOM */
    updateOutletPersonalization(
        distributionId: number,
        outletUuid: string,
        personalization: PersonalizationValue
    ): Observable<void> {
        const slug: string = `/distributions/${distributionId}/personalizations/outlet/${outletUuid}`;

        return this.neptuneApi.api<void>(
            slug,
            personalization,
            HttpVerbsEnum.PUT
        );
    }


    /** Only applies to PersonalizationSourceTypeEnum.CUSTOM */
    deleteOutletPersonalization(
        distributionId: number,
        outletUuid: string,
        tagType: PersonalizationTagEnum
    ): Observable<void> {
        const slug: string = `/distributions/${distributionId}/personalizations/outlet/${outletUuid}?tagType=${tagType}`;

        return this.neptuneApi.api<void>(
            slug,
            null,
            HttpVerbsEnum.DELETE
        );
    }

    /** Only applies to PersonalizationSourceTypeEnum.CUSTOM */
    deleteContactPersonalization(
        distributionId: number,
        contactUuid: string,
        tagType: PersonalizationTagEnum
    ): Observable<void> {
        const slug: string = `/distributions/${distributionId}/personalizations/contact/${contactUuid}?tagType=${tagType}`;

        return this.neptuneApi.api<void>(
            slug,
            null,
            HttpVerbsEnum.DELETE
        );
    }


    upsertOutletPersonalizationSelections(
        distributionId: number,
        outletUuid: string,
        selectionModel: PersonalizationSelection
    ): Observable<void> {
        const slug: string = `/distributions/${distributionId}/personalizations/outlet/${outletUuid}/selection`;

        return this.neptuneApi.api<void>(
            slug,
            selectionModel,
            HttpVerbsEnum.PUT
        );
    }

    upsertContactPersonalizationSelections(
        distributionId: number,
        contactUuid: string,
        selectionModel: PersonalizationSelection
    ): Observable<void> {
        const slug: string = `/distributions/${distributionId}/personalizations/contact/${contactUuid}/selection`;

        return this.neptuneApi.api<void>(
            slug,
            selectionModel,
            HttpVerbsEnum.PUT
        );
    }



    getPersonalizationStats(distributionId: number, includeUnlockedSearch: boolean): Observable<PersonalizationDetailsApiModel> {
        const slug: string = `/distributions/${distributionId}/distributions/${distributionId}?includeUnlockedSearches=${includeUnlockedSearch}`;

        return this.neptuneApi.api<PersonalizationDetailsApiModel>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    getRecommendedRecipientQuery(id: number): Observable<any> {
        const slug: string = `/distributions/${id}/recommendedrecipientsquery`;

        return this.neptuneApi.api<any>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    getQuickEditQuery(id: number): Observable<any> {
        const slug: string = `/distributions/${id}/recommendedrecipientsquery?isQuickEdit=true`;

        return this.neptuneApi.api<any>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    performRecommendedRecipientSearch(
        search: TargetingQueryApi,
        start: number,
        rows: number,
        sortOrder?: SortOrderEnum,
        sortField?: string
    ): Observable<TargetingSearchResponseApiModel> {
        const slug: string = '/distributions/recommendedrecipients';

        const params = {
            rows: rows,
            start: start,
            sortBy: sortField,
            sortOrder: sortOrder,
            locale: this.locale
        } as any;

        if (!sortOrder) {
            delete params.sortOrder;
        }
        if (!sortField) {
            delete params.sortBy;
        }

        return this.neptuneApi.api<TargetingSearchResponseApiModel>(
            slug,
            search,
            HttpVerbsEnum.POST,
            params
        );
    }

    getAEORecommendations(id: number): Observable<AEORecommendationsApiModel> {
        const slug = `/distributions/${id}/aeo`;

        return this.neptuneApi.api<AEORecommendationsApiModel>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }
}
